/**
 * CASE.SHE — Добірка колекції.
 *
 * Мінімальне керування горизонтальною каруселлю поверх нативного scroll-snap:
 *   - стрілки гортають на одну картку;
 *   - на краях відповідна стрілка стає disabled;
 *   - якщо всі товари вміщуються, блок стрілок ховається;
 *   - гортання пальцем і клавіатурою лишається нативним, скрол робить браузер.
 *
 * Слухачі та ResizeObserver прибираються у `disconnectedCallback`, тож повторне
 * завантаження чи видалення секції в Theme Editor нічого не лишає позаду.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class CaseSheFeaturedCarousel extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {ResizeObserver | undefined} */
  #observer;

  connectedCallback() {
    this.viewport = this.querySelector('[data-cs-fc-viewport]');
    if (!this.viewport) return;

    this.previousButton = this.querySelector('[data-cs-fc-prev]');
    this.nextButton = this.querySelector('[data-cs-fc-next]');

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.previousButton?.addEventListener('click', () => this.#step(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.#step(1), { signal });
    this.viewport.addEventListener('scroll', this.#update, { passive: true, signal });

    if (typeof ResizeObserver !== 'undefined') {
      this.#observer = new ResizeObserver(this.#update);
      this.#observer.observe(this.viewport);
    }

    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = undefined;
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /** Ширина одного кроку: картка плюс проміжок між картками. */
  get #stepSize() {
    const item = this.viewport.querySelector('[data-cs-fc-item]');
    if (!item) return this.viewport.clientWidth;

    const gap = Number.parseFloat(getComputedStyle(this.viewport).columnGap);
    return item.getBoundingClientRect().width + (Number.isFinite(gap) ? gap : 0);
  }

  /**
   * @param {number} direction - -1 назад, 1 вперед.
   */
  #step(direction) {
    const behavior = window.matchMedia(REDUCED_MOTION_QUERY).matches ? 'instant' : 'smooth';
    this.viewport.scrollBy({ left: this.#stepSize * direction, behavior });
  }

  #update = () => {
    const { scrollWidth, clientWidth, scrollLeft } = this.viewport;
    const maxScroll = scrollWidth - clientWidth;

    // Один піксель допуску: браузери округлюють scrollLeft дробово.
    this.toggleAttribute('data-overflowing', maxScroll > 1);

    if (this.previousButton) this.previousButton.disabled = scrollLeft <= 1;
    if (this.nextButton) this.nextButton.disabled = scrollLeft >= maxScroll - 1;
  };
}

if (!customElements.get('case-she-featured-carousel')) {
  customElements.define('case-she-featured-carousel', CaseSheFeaturedCarousel);
}
